import { useRef, useState, useCallback, useEffect } from 'react';
import { useAppStore } from '@/lib/store';
import { StepProgress } from './ui/StepProgress';
import { PoseOverlay } from './PoseOverlay';
import { RealtimePose } from '@/lib/realtime-pose';
import { useBeep } from '@/lib/use-beep';
import { useVoiceGuide } from '@/lib/use-voice-guide';
import type { PoseFrame } from '@/lib/realtime-pose';

const STEPS = ['Clothing', 'Height', 'Photo', 'Results'];

type CaptureState = 'idle' | 'loading' | 'ready' | 'preview';
type FacingMode = 'user' | 'environment';
type SelfTimerDuration = 0 | 5 | 10 | 15;

/** Seconds of stability required before auto-capture countdown starts. */
const STABILITY_REQUIRED = 1.5;
/** Countdown duration (3-2-1). */
const COUNTDOWN_SECONDS = 3;

/** Self-timer duration options (seconds). 0 = off. */
const SELF_TIMER_OPTIONS: SelfTimerDuration[] = [0, 5, 10, 15];

/** Beep frequencies and durations */
const TICK_BEEP = { freq: 800, ms: 100 } as const;
const FINAL_BEEP = { freq: 1200, ms: 300 } as const;

interface CameraCaptureProps {
  captureType?: 'front' | 'side';
}

export function CameraCapture({ captureType = 'front' }: CameraCaptureProps) {
  const { setFrontPhoto, setSidePhoto, setStep } = useAppStore();
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const poseRef = useRef<RealtimePose | null>(null);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [captureState, setCaptureState] = useState<CaptureState>('idle');
  const [preview, setPreview] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [facing, setFacing] = useState<FacingMode>('environment');
  const [usedCamera, setUsedCamera] = useState(false);

  // Real-time pose state
  const [poseFrame, setPoseFrame] = useState<PoseFrame | null>(null);
  const [modelLoading, setModelLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [videoNativeSize, setVideoNativeSize] = useState({ w: 0, h: 0 });

  // Self-timer state
  const [selfTimerDuration, setSelfTimerDuration] = useState<SelfTimerDuration>(0);
  const [selfTimerCountdown, setSelfTimerCountdown] = useState(0);
  const [selfTimerActive, setSelfTimerActive] = useState(false);
  const selfTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { playBeep, dispose: disposeBeep } = useBeep();

  // Voice guide
  const [voiceGuideEnabled, setVoiceGuideEnabled] = useState(true);
  const voiceGuide = useVoiceGuide();

  // Track if countdown auto-captured to prevent double captures
  const autoCapturedRef = useRef(false);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      poseRef.current?.dispose();
      poseRef.current = null;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
      if (selfTimerRef.current) {
        clearInterval(selfTimerRef.current);
      }
      disposeBeep();
      voiceGuide.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Voice guide: announce camera ready ──
  useEffect(() => {
    if (!voiceGuideEnabled || captureState !== 'ready') return;
    const msg = captureType === 'side'
      ? 'Great! Now let\'s get your side view. Just turn sideways naturally, arms relaxed!'
      : 'Alright! Let\'s get your measurements. Stand tall with your full body in frame!';
    voiceGuide.speak(msg, 'capture');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureState === 'ready', voiceGuideEnabled]);

  // ── Voice guide alignment feedback ──
  useEffect(() => {
    if (!voiceGuideEnabled || !poseFrame || captureState !== 'ready') return;
    if (selfTimerActive || countdown > 0) return; // Don't interrupt countdowns

    const { alignment, stableSeconds } = poseFrame;

    if (alignment.distance === 'too_close') {
      voiceGuide.speak('Just step back a bit for me!');
    } else if (alignment.distance === 'too_far') {
      voiceGuide.speak('Come a little closer, you\'re doing great!');
    } else if (!alignment.head) {
      voiceGuide.speak('I need to see your whole self! Show me that head!');
    } else if (!alignment.legs) {
      voiceGuide.speak('Almost there! Let me see those legs too!');
    } else if (!alignment.shoulders || !alignment.torso) {
      voiceGuide.speak(
        captureType === 'side'
          ? 'Turn a bit more sideways for me!'
          : 'Face me straight on, looking good!'
      );
    } else if (alignment.allAligned && stableSeconds >= 0.5 && stableSeconds < 1.5) {
      voiceGuide.speak(
        captureType === 'side' ? 'Beautiful profile! Hold it right there!' : 'Perfect! Hold that pose, champion!'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poseFrame, captureState, voiceGuideEnabled, selfTimerActive, countdown]);

  // ── Auto-capture countdown logic ──
  useEffect(() => {
    // Disable pose auto-capture while self-timer is counting down
    if (selfTimerActive) return;

    if (!poseFrame || captureState !== 'ready') {
      // Reset countdown if pose lost or not ready
      if (countdown > 0) {
        setCountdown(0);
        if (countdownTimerRef.current) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
        }
      }
      return;
    }

    const { stableSeconds } = poseFrame;

    // Start countdown when stable long enough
    if (stableSeconds >= STABILITY_REQUIRED && countdown === 0 && !autoCapturedRef.current) {
      setCountdown(COUNTDOWN_SECONDS);
      if (voiceGuideEnabled) voiceGuide.speak(String(COUNTDOWN_SECONDS), 'capture');

      let tick = COUNTDOWN_SECONDS;
      countdownTimerRef.current = setInterval(() => {
        tick -= 1;
        if (tick <= 0) {
          if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
          }
          setCountdown(0);
          autoCapturedRef.current = true;
          doCapture();
        } else {
          setCountdown(tick);
          if (voiceGuideEnabled) voiceGuide.speak(String(tick), 'capture');
        }
      }, 1000);
    }

    // If user moved during countdown, cancel it
    if (stableSeconds < 0.5 && countdown > 0) {
      setCountdown(0);
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poseFrame?.stableSeconds, captureState, selfTimerActive]);

  const stopCamera = useCallback(() => {
    poseRef.current?.stop();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setVideoNativeSize({ w: 0, h: 0 });
  }, []);

  const startCamera = useCallback(
    async (facingMode: FacingMode = facing) => {
      setCameraError(null);
      setCaptureState('loading');
      autoCapturedRef.current = false;
      setCountdown(0);
      setPoseFrame(null);
      stopCamera();

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facingMode },
            width: { ideal: 1080 },
            height: { ideal: 1920 },
          },
          audio: false,
        });

        streamRef.current = stream;

        const video = videoRef.current;
        if (!video) {
          setCaptureState('idle');
          return;
        }

        video.srcObject = stream;

        // Wait for video metadata
        await new Promise<void>((resolve, reject) => {
          const onLoaded = () => {
            video.removeEventListener('loadedmetadata', onLoaded);
            video.removeEventListener('error', onError);
            resolve();
          };
          const onError = () => {
            video.removeEventListener('loadedmetadata', onLoaded);
            video.removeEventListener('error', onError);
            reject(new Error('Video stream failed to load'));
          };
          if (video.readyState >= 1) {
            resolve();
            return;
          }
          video.addEventListener('loadedmetadata', onLoaded);
          video.addEventListener('error', onError);
        });

        await video.play();
        setVideoNativeSize({ w: video.videoWidth, h: video.videoHeight });
        setCaptureState('ready');
        setUsedCamera(true);

        // Try fullscreen on mobile
        try {
          const el = containerRef.current;
          await (el?.requestFullscreen?.() ??
            (el as HTMLDivElement & { webkitRequestFullscreen?: () => Promise<void> })?.webkitRequestFullscreen?.());
        } catch {
          // Fine — fixed positioning handles it
        }

        // Start real-time pose detection (load model if needed)
        if (!poseRef.current) {
          poseRef.current = new RealtimePose((frame) => setPoseFrame(frame), captureType);
        }

        if (!poseRef.current.isReady && !poseRef.current.isLoading) {
          setModelLoading(true);
          await poseRef.current.load();
          setModelLoading(false);
        }

        poseRef.current.start(video);
      } catch (err) {
        stopCamera();
        setCaptureState('idle');

        if (err instanceof DOMException && err.name === 'NotAllowedError') {
          setCameraError(
            'Camera permission denied. Please allow camera access in your browser settings, or use "Upload Photo" below.'
          );
        } else if (err instanceof DOMException && err.name === 'NotFoundError') {
          setCameraError('No camera found. Please use "Upload Photo" instead.');
        } else {
          setCameraError('Could not start camera. Please use "Upload Photo" instead.');
        }
      }
    },
    [facing, stopCamera]
  );

  function switchCamera() {
    const next: FacingMode = facing === 'environment' ? 'user' : 'environment';
    setFacing(next);
    startCamera(next);
  }

  function cycleSelfTimer() {
    const currentIndex = SELF_TIMER_OPTIONS.indexOf(selfTimerDuration);
    const nextIndex = (currentIndex + 1) % SELF_TIMER_OPTIONS.length;
    setSelfTimerDuration(SELF_TIMER_OPTIONS[nextIndex]!);
  }

  function startSelfTimer() {
    const duration = selfTimerDuration;
    if (duration === 0) {
      doCapture();
      return;
    }

    // Cancel any existing pose countdown
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setCountdown(0);

    setSelfTimerActive(true);
    setSelfTimerCountdown(duration);
    playBeep(TICK_BEEP.freq, TICK_BEEP.ms);
    if (voiceGuideEnabled) voiceGuide.speak('Timer started! Get into position, you got this!', 'capture');

    let remaining = duration;
    selfTimerRef.current = setInterval(() => {
      remaining -= 1;
      setSelfTimerCountdown(remaining);

      if (remaining <= 0) {
        if (selfTimerRef.current) {
          clearInterval(selfTimerRef.current);
          selfTimerRef.current = null;
        }
        playBeep(FINAL_BEEP.freq, FINAL_BEEP.ms);
        setSelfTimerActive(false);
        setSelfTimerCountdown(0);
        doCapture();
      } else {
        // Higher pitch for last 3 seconds
        const freq = remaining <= 3 ? 1000 : TICK_BEEP.freq;
        playBeep(freq, TICK_BEEP.ms);
        // Voice countdown for final 3 seconds
        if (voiceGuideEnabled && remaining <= 3) {
          voiceGuide.speak(String(remaining), 'capture');
        }
      }
    }, 1000);
  }

  function cancelSelfTimer() {
    if (selfTimerRef.current) {
      clearInterval(selfTimerRef.current);
      selfTimerRef.current = null;
    }
    setSelfTimerActive(false);
    setSelfTimerCountdown(0);
  }

  /** Core capture logic — used by both manual button and auto-capture. */
  function doCapture() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const w = video.videoWidth;
    const h = video.videoHeight;

    if (w === 0 || h === 0) {
      setCameraError('Camera is still loading. Please wait a moment.');
      return;
    }

    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);

    setPreview(dataUrl);
    setCaptureState('preview');
    setCountdown(0);
    setPoseFrame(null);
    stopCamera();

    if (voiceGuideEnabled) voiceGuide.speak('Got it! Looking sharp!', 'capture');

    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
  }

  function handleManualCapture() {
    // Cancel any running pose countdown
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setCountdown(0);

    if (selfTimerDuration > 0 && !selfTimerActive) {
      startSelfTimer();
    } else if (selfTimerActive) {
      cancelSelfTimer();
    } else {
      doCapture();
    }
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setCameraError('Please select an image file.');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setCameraError('Image is too large. Please use a photo under 20MB.');
      return;
    }

    setCameraError(null);
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result as string);
      setCaptureState('preview');
    };
    reader.onerror = () => setCameraError('Failed to read the image file.');
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  function handleUsePhoto() {
    if (preview) {
      if (captureType === 'side') {
        setSidePhoto(preview);
        setStep('processing_side');
      } else {
        setFrontPhoto(preview);
        setStep('processing');
      }
    }
  }

  function handleRetake() {
    setPreview(null);
    setCaptureState('idle');
    autoCapturedRef.current = false;
    cancelSelfTimer();
    if (usedCamera) {
      startCamera();
    }
  }

  // ── Preview state ── full-screen photo review ──────────────────
  if (captureState === 'preview' && preview) {
    return (
      <div className="camera-container">
        <img
          src={preview}
          alt="Your photo"
          className="camera-feed"
          style={{ objectFit: 'contain' }}
        />

        {/* Top info */}
        <div
          className="absolute left-0 right-0 top-0 z-[3] bg-gradient-to-b from-black/70 to-transparent px-4 pb-10 pt-4"
          style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}
        >
          <p className="text-center text-base font-semibold text-white">
            Looking good?
          </p>
          <p className="mt-1 text-center text-sm text-white/70">
            Make sure your full body is visible head to toe.
          </p>
        </div>

        {/* Bottom controls */}
        <div className="camera-controls">
          <div className="mx-auto flex w-full max-w-sm gap-3 px-4">
            <button
              onClick={handleRetake}
              className="flex-1 rounded-xl border-2 border-white/40 px-4 py-3.5 text-base font-semibold text-white active:scale-[0.97] transition-transform"
            >
              Retake
            </button>
            <button
              onClick={handleUsePhoto}
              className="flex-1 rounded-xl bg-brand-500 px-4 py-3.5 text-base font-semibold text-white active:scale-[0.97] transition-transform"
            >
              Use This Photo
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Camera active state ── full-screen viewfinder ──────────────
  if (captureState === 'loading' || captureState === 'ready') {
    return (
      <div ref={containerRef} className="camera-container">
        {/* Video fills entire viewport */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="camera-feed"
        />

        {/* Loading overlay (camera starting) */}
        {captureState === 'loading' && (
          <div className="absolute inset-0 z-[5] flex items-center justify-center bg-black/80">
            <div className="text-center">
              <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-white/30 border-t-white" />
              <p className="mt-4 text-base text-white/80">Starting camera...</p>
            </div>
          </div>
        )}

        {/* Pose overlay — live skeleton + alignment + countdown */}
        {captureState === 'ready' && (
          <PoseOverlay
            frame={poseFrame}
            videoWidth={videoNativeSize.w}
            videoHeight={videoNativeSize.h}
            modelLoading={modelLoading}
            countdown={countdown}
            selfTimerCountdown={selfTimerCountdown}
            voiceGuideActive={voiceGuideEnabled}
            captureType={captureType}
          />
        )}

        {/* Top hint */}
        {captureState === 'ready' && countdown === 0 && !selfTimerActive && (
          <div
            className="absolute left-0 right-0 top-0 z-[3] bg-gradient-to-b from-black/60 to-transparent px-4 pb-8 pt-4"
            style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}
          >
            <p className="text-center text-sm font-medium text-white/90">
              {poseFrame?.alignment.allAligned
                ? 'Hold still — auto-capturing...'
                : captureType === 'side'
                  ? 'Stand sideways with your full body visible'
                  : 'Stand back so your full body is visible'}
            </p>
          </div>
        )}

        {cameraError && (
          <div className="absolute bottom-28 left-4 right-4 z-[4]">
            <p className="rounded-lg bg-red-500/90 px-4 py-2 text-center text-sm text-white">
              {cameraError}
            </p>
          </div>
        )}

        {/* Bottom controls — normal mode */}
        {captureState === 'ready' && countdown === 0 && !selfTimerActive && (
          <div className="camera-controls">
            {/* Switch camera */}
            <button
              onClick={switchCamera}
              className="rounded-full bg-black/50 p-3.5 text-white backdrop-blur-sm active:bg-black/70"
              aria-label="Switch camera"
            >
              <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 9l3-3m0 0l3 3m-3-3v12m15-6l-3 3m0 0l-3-3m3 3V6" />
              </svg>
            </button>

            {/* Capture button with timer + voice pills */}
            <div className="flex flex-col items-center gap-2">
              {/* Timer and voice toggle pills */}
              <div className="flex items-center gap-2">
                {/* Timer toggle pill */}
                <button
                  onClick={cycleSelfTimer}
                  className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium backdrop-blur-sm transition-colors ${
                    selfTimerDuration > 0
                      ? 'bg-brand-500/80 text-white'
                      : 'bg-black/40 text-white/70'
                  }`}
                  aria-label={`Self-timer: ${selfTimerDuration === 0 ? 'off' : selfTimerDuration + ' seconds'}`}
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
                  </svg>
                  {selfTimerDuration === 0 ? 'Off' : `${selfTimerDuration}s`}
                </button>

                {/* Voice guide toggle pill */}
                {voiceGuide.isSupported && (
                  <button
                    onClick={() => setVoiceGuideEnabled(!voiceGuideEnabled)}
                    className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium backdrop-blur-sm transition-colors ${
                      voiceGuideEnabled
                        ? 'bg-brand-500/80 text-white'
                        : 'bg-black/40 text-white/70'
                    }`}
                    aria-label={`Voice guide: ${voiceGuideEnabled ? 'on' : 'off'}`}
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round"
                        d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M6 9H4a1 1 0 00-1 1v4a1 1 0 001 1h2l3.5 3.5V5.5L6 9z" />
                    </svg>
                    {voiceGuideEnabled ? 'Voice' : 'Voice'}
                  </button>
                )}
              </div>

              {/* Capture ring */}
              <button
                onClick={handleManualCapture}
                className="rounded-full bg-white p-1.5 shadow-lg active:scale-90 transition-transform"
                aria-label={selfTimerDuration > 0 ? `Take photo with ${selfTimerDuration}s timer` : 'Take photo'}
              >
                <div className="h-[68px] w-[68px] rounded-full border-[5px] border-brand-500" />
              </button>
            </div>

            {/* Cancel */}
            <button
              onClick={() => {
                stopCamera();
                setCaptureState('idle');
                cancelSelfTimer();
                if (document.fullscreenElement) {
                  document.exitFullscreen().catch(() => {});
                }
              }}
              className="rounded-full bg-black/50 p-3.5 text-white backdrop-blur-sm active:bg-black/70"
              aria-label="Cancel"
            >
              <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Bottom controls — self-timer active */}
        {captureState === 'ready' && selfTimerActive && (
          <div className="camera-controls">
            <button
              onClick={cancelSelfTimer}
              className="rounded-full bg-red-500/80 px-6 py-3 text-base font-semibold text-white backdrop-blur-sm active:bg-red-600"
            >
              Cancel Timer
            </button>
          </div>
        )}

        <canvas ref={canvasRef} className="hidden" />
      </div>
    );
  }

  // ── Idle state (tips + open camera / upload) ───────────────────
  return (
    <div className="screen">
      <StepProgress steps={STEPS} currentIndex={2} />

      <div className="mt-6 flex-1">
        <h2 className="text-2xl font-bold text-gray-900">
          {captureType === 'side' ? 'Take a side photo' : 'Take a front photo'}
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          {captureType === 'side'
            ? 'Stand sideways naturally — arms at your sides is fine!'
            : 'Stand straight, arms slightly away from body, full body visible.'}
        </p>

        <div className="mt-4 space-y-4">
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-900">
              Tips for best results:
            </h3>
            {captureType === 'side' ? (
              <ul className="mt-2 space-y-1 text-sm text-gray-500">
                <li>- Stand in a well-lit area</li>
                <li>- Full body visible (head to feet)</li>
                <li>- Turn sideways to the camera</li>
                <li>- Arms at your sides is fine</li>
                <li>- Stand naturally — no stretching needed</li>
                <li>- Ask someone to take the photo, or use a timer</li>
              </ul>
            ) : (
              <ul className="mt-2 space-y-1 text-sm text-gray-500">
                <li>- Stand in a well-lit area</li>
                <li>- Full body visible (head to feet)</li>
                <li>- Arms slightly away from body</li>
                <li>- Face the camera directly</li>
                <li>- Solid background if possible</li>
                <li>- Ask someone to take the photo, or use a timer</li>
              </ul>
            )}
          </div>

          {cameraError && (
            <p className="text-sm text-red-500">{cameraError}</p>
          )}

          <button onClick={() => startCamera()} className="btn-primary">
            Open Camera
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-surface-secondary px-2 text-gray-400">or</span>
            </div>
          </div>

          <button
            onClick={() => fileInputRef.current?.click()}
            className="btn-secondary"
          >
            Upload Photo
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      </div>

      <div className="mt-6">
        <button onClick={() => setStep('reference')} className="btn-secondary">
          Back
        </button>
      </div>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
